import CodeIcon from '@mui/icons-material/Code'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import { DigitalTwinSettingTarget } from 'api/digitalTwin/digitalTwin.api'
import { ReactNode, useEffect, useId, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { CreateSettingButton } from 'ui/components/DigitalTwinSettingEditor/CreateSettingButton'
import { getNewPropertySetting } from 'ui/components/DigitalTwinSettingEditor/editorSetting.helper'
import {
  getEditorTitle,
  getPropertyHelpText,
  getPropertyTitle,
} from 'ui/components/DigitalTwinSettingEditor/editorTitle.helper'
import { useTimeSeriesSettingAccess } from 'ui/components/DigitalTwinSettingForm/useTimeSeriesSettingAccess'
import Tooltip from 'ui/components/Tooltip/Tooltip'
import type { ShownInSplitView, SplitViewSelection } from 'views/DigitalTwinSettingsView/settingEditor.types'
import { PropertySettings } from './PropertySettings'
import { SettingFormPane } from './SettingFormPane'
import { SplitViewLayout } from './SplitViewLayout'
import type { PaneProps, SettingEditorContext } from './splitViewPane.types'
import { getShownInSplitView, useSplitViewEditor } from './useSplitViewEditor'
import { SETTING_SPLIT_VIEW_POSITION } from './useSplitViewPosition'
import {
  MAX_SPLIT_VIEW_WIDTH_PERCENT,
  MIN_SPLIT_VIEW_WIDTH_PERCENT,
  SETTING_SPLIT_VIEW_OVERLAY_SHADOW,
  SETTING_SPLIT_VIEW_WIDTH_CLASS,
  SplitViewResizeHandlers,
} from './useSplitViewWidth'

/** Under the view's sticky header, so the dropdowns that open from it stay usable, and over the main view's content. */
const SETTING_SPLIT_VIEW_Z_INDEX = 2
/** The app's card radius (`@border-main`). */
const SETTING_SPLIT_VIEW_RADIUS = '0.375rem'

type SettingSplitViewProps = SettingEditorContext & {
  selection: SplitViewSelection | null
  selectionRevision: number
  onClose: () => void
  /** Told what the split view shows whenever that changes, e.g. the setting a save stored. */
  onShownChange?: (shownInSplitView: ShownInSplitView) => void
  resize: SplitViewResizeHandlers
}

/**
 * Edits digital twin settings beside the main view. It stays open after a save and follows what the
 * user selects in the main view; only its close button, Cancel and Remove close it.
 */
export function SettingSplitView({
  selection,
  selectionRevision,
  onClose,
  onShownChange,
  resize,
  ...context
}: SettingSplitViewProps): ReactNode {
  const { t } = useTranslation()
  const titleId = useId()
  const widthToggleLabel = resize.togglesToNarrow ? t('Narrow split view') : t('Widen split view')
  const widthToggle = (
    <div className="hidden lg:block">
      <Tooltip title={widthToggleLabel} arrow>
        <IconButton size="small" aria-label={widthToggleLabel} onClick={resize.toggleNarrow}>
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </div>
  )

  return (
    <Drawer
      variant="persistent"
      anchor="right"
      open={selection !== null}
      slotProps={{
        paper: {
          className: SETTING_SPLIT_VIEW_WIDTH_CLASS,
          sx: {
            overflow: 'hidden',
            zIndex: SETTING_SPLIT_VIEW_Z_INDEX,
            borderRadius: SETTING_SPLIT_VIEW_RADIUS,
            ...SETTING_SPLIT_VIEW_POSITION,
            ...SETTING_SPLIT_VIEW_OVERLAY_SHADOW,
          },
          role: 'complementary',
          'aria-labelledby': titleId,
        },
      }}
    >
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={t('Resize split view')}
        aria-valuenow={Math.round(resize.widthPercent)}
        aria-valuemin={MIN_SPLIT_VIEW_WIDTH_PERCENT}
        aria-valuemax={MAX_SPLIT_VIEW_WIDTH_PERCENT}
        tabIndex={0}
        onMouseDown={resize.onResizeStart}
        onKeyDown={resize.onResizeKeyDown}
        className="group absolute inset-y-0 left-0 z-10 hidden w-2.5 cursor-col-resize items-center justify-center hover:bg-grey-3/60 focus-visible:bg-grey-3 lg:flex"
      >
        <span aria-hidden className="h-12 w-1 rounded-full bg-grey-4 group-hover:bg-grey-6" />
      </div>
      {selection && (
        <div
          className="h-full"
          data-testid="deviation-setting-dialog" // e2e: settings dialog selector — do not remove
        >
          <SettingSplitViewEditor
            key={selectionRevision}
            selection={selection}
            selectionRevision={selectionRevision}
            onShownChange={onShownChange}
            titleId={titleId}
            onClose={onClose}
            headerActions={widthToggle}
            {...context}
          />
        </div>
      )}
    </Drawer>
  )
}

function SettingSplitViewEditor({
  selection,
  selectionRevision,
  onShownChange,
  ...paneProps
}: Omit<PaneProps, 'editor'> &
  Pick<SettingSplitViewProps, 'selectionRevision' | 'onShownChange'> & { selection: SplitViewSelection }): ReactNode {
  const editor = useSplitViewEditor(selection)

  useEffect(() => {
    onShownChange?.(getShownInSplitView(editor.pane, selectionRevision))
  }, [editor.pane, onShownChange, selectionRevision])

  return editor.pane.pane === 'property' ? (
    <PropertySettingsPane {...paneProps} editor={editor} property={editor.pane.property} />
  ) : (
    <SettingFormPane {...paneProps} editor={editor} form={editor.pane} />
  )
}

function PropertySettingsPane({
  property,
  editor,
  titleId,
  onClose,
  headerActions,
  ...context
}: PaneProps & { property: DigitalTwinSettingTarget }): ReactNode {
  const { t } = useTranslation()
  const hasAccessToTimeseries = useTimeSeriesSettingAccess()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const { digitalTwin, isBaseSettings } = context

  return (
    <SplitViewLayout
      title={getEditorTitle({ propertyTitle: getPropertyTitle(property, digitalTwin), editedSetting: null, t })}
      helpText={getPropertyHelpText(property, digitalTwin)}
      titleId={titleId}
      onClose={onClose}
      headerActions={headerActions}
      scrollContainerRef={scrollContainerRef}
    >
      <div className="flex justify-end px-6 pt-4">
        <CreateSettingButton
          allowTimeSeries={hasAccessToTimeseries && !isBaseSettings}
          disabled={!context.permissions.canEdit}
          onCreate={(isTimeSeries) => editor.openForm(getNewPropertySetting({ ...context, property, isTimeSeries }))}
        />
      </div>
      <PropertySettings
        {...context}
        property={property}
        revision={editor.historyRevision}
        onEditSetting={editor.editSetting}
      />
    </SplitViewLayout>
  )
}
